import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ShieldOff, Cpu, MemoryStick, HardDrive, BatteryCharging, Battery,
  Activity, RefreshCw, Terminal, Server, AlertTriangle, Info,
  Power, Lock, RefreshCcw, Send, Play, Calendar, Network,
  Package, FileText, Download, Plus, Trash2, BookOpen, Wifi, User, Clock,
  Thermometer, Shield, Bell, MapPin, MonitorSmartphone,
} from "lucide-react";
import {
  getDevice,
  dispatchRmmCommand, pollRmmCommand,
  getDeviceAlerts, resolveDeviceAlert,
  getRmmScripts, saveRmmScript, deleteRmmScript,
  getDeviceToolCache,
} from "../api";
import RemoteButton from "../components/RemoteButton";
import useIsMobile from "../hooks/useIsMobile";
import MacAgentInstall from "../components/MacAgentInstall";

// Cyber theme — surfaces/borders re-skinned to deep navy + neon-blue edges.
// Status hues (green/amber/red) are kept so the CPU/RAM/disk usage meters and
// status pills keep their meaning; blue/violet accents bumped to neon.
const C = {
  bgPage:"#050b1a", bgWindow:"#0b1730", bgCard:"#0d1a36", bgCardHi:"#14264a",
  bdSoft:"rgba(96,124,255,0.20)", bdMid:"rgba(96,124,255,0.34)",
  txtPri:"#f4f8ff", txtSec:"#cdd9ef", txtMut:"#93a6c9", txtFaint:"#6f86ab",
  blue:"#4f6fff", blue4:"#7088ff",
  green:"#22c55e", greenSoft:"#6ee7b7",
  red:"#ef4444", redSoft:"#fca5a5",
  amber:"#f59e0b", amberSoft:"#fde68a",
  violet:"#9d8bff",
};

const timeSince = (ts) => {
  if (!ts) return "—";
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  return s < 60 ? `${s}s ago` : s < 3600 ? `${~~(s/60)}m ago` : s < 86400 ? `${~~(s/3600)}h ago` : `${~~(s/86400)}d ago`;
};

function pctColor(v) {
  if (v == null) return C.txtFaint;
  if (v >= 90) return C.red;
  if (v >= 75) return C.amber;
  return C.green;
}

function Pill({ color, children }) {
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", height:18, padding:"0 8px", borderRadius:99,
      fontSize:10, fontWeight:600, letterSpacing:"0.02em", whiteSpace:"nowrap",
      background:color+"22", color,
    }}>{children}</span>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize:10, fontWeight:600, color:C.txtFaint,
      textTransform:"uppercase", letterSpacing:"0.08em", margin:"4px 4px 8px",
    }}>{children}</div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{
      background:C.bgCard, border:`1px solid ${C.bdSoft}`,
      borderRadius:10, padding:16, boxSizing:"border-box", width:"100%", ...style,
    }}>{children}</div>
  );
}

// ─── Stat card used in vitals panel ──────────────────────────────────────────
function StatCard({ icon: Icon, label, value, sub, pct, color }) {
  const barColor = color || pctColor(pct);
  return (
    <div style={{
      background:C.bgCard, border:`1px solid ${C.bdSoft}`, borderRadius:10,
      padding:"12px 14px", flex:"1 1 150px", minWidth:130, boxSizing:"border-box",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
        <Icon size={13} color={C.txtFaint}/>
        <span style={{ fontSize:10, fontWeight:600, color:C.txtFaint, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</span>
      </div>
      <div style={{ fontSize:26, fontWeight:700, color: pct != null ? barColor : C.txtSec, lineHeight:1, marginBottom:6 }}>
        {value}
      </div>
      {pct != null && (
        <div style={{ height:4, borderRadius:99, background:C.bdMid, marginBottom:6, overflow:"hidden" }}>
          <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:barColor, borderRadius:99, transition:"width 0.4s" }}/>
        </div>
      )}
      {sub && <div style={{ fontSize:11, color:C.txtFaint }}>{sub}</div>}
    </div>
  );
}

// ─── Drive bar ───────────────────────────────────────────────────────────────
function DriveBar({ drive }) {
  const pct = drive.used_percent ?? 0;
  const col = pctColor(pct);
  return (
    <div style={{ marginBottom:8, lastChild:{ marginBottom:0 } }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:12 }}>
        <span style={{ color:C.txtSec, fontFamily:"monospace", fontWeight:600 }}>
          {drive.name}:{drive.label ? ` ${drive.label}` : ""}
        </span>
        <span style={{ color:col, fontWeight:600 }}>{pct.toFixed(0)}%</span>
      </div>
      <div style={{ height:6, borderRadius:99, background:C.bdMid, overflow:"hidden", marginBottom:3 }}>
        <div style={{ height:"100%", width:`${Math.min(pct,100)}%`, background:col, borderRadius:99 }}/>
      </div>
      <div style={{ fontSize:11, color:C.txtFaint }}>
        {drive.used_gb?.toFixed(1)} GB used / {drive.total_gb?.toFixed(1)} GB total · {drive.free_gb?.toFixed(1)} GB free
      </div>
    </div>
  );
}

// ─── Vitals panel (always-visible above tabs) ─────────────────────────────
function VitalsPanel({ device }) {
  const agentOnline = device.last_seen && (Date.now() - new Date(device.last_seen).getTime()) < 48 * 3600_000;
  const h = device; // health fields from LATERAL join

  // Parse all_disks — comes back as JSON string or object
  let allDisks = [];
  try {
    if (h.all_disks) allDisks = typeof h.all_disks === "string" ? JSON.parse(h.all_disks) : h.all_disks;
  } catch {}

  // Parse health_alerts
  let alertList = [];
  try {
    if (h.health_alerts) alertList = typeof h.health_alerts === "string" ? JSON.parse(h.health_alerts) : h.health_alerts;
  } catch {}

  const hasVitals = h.cpu_load_percent != null || h.ram_used_percent != null;
  const hasBattery = h.battery_present;
  const hasNetwork = h.net_sent_mb != null || h.net_recv_mb != null;
  const hasAllDisks = allDisks.length > 1;

  const ramTotal = h.health_ram_total_gb ?? h.ram_total_gb;
  const ramUsed  = h.health_ram_used_gb  ?? h.ram_used_gb;

  return (
    <div style={{ marginBottom:16 }}>
      {/* Agent status row */}
      <div style={{
        display:"flex", alignItems:"center", gap:12, padding:"10px 16px",
        background: agentOnline ? "rgba(34,197,94,0.06)" : "rgba(148,163,184,0.05)",
        border:`1px solid ${agentOnline ? "rgba(34,197,94,0.25)" : "rgba(148,163,184,0.15)"}`,
        borderRadius:10, marginBottom:12,
      }}>
        <span style={{
          width:8, height:8, borderRadius:"50%", flexShrink:0,
          background: agentOnline ? C.green : C.txtDeep,
          boxShadow: agentOnline ? `0 0 6px ${C.green}` : "none",
        }}/>
        <div style={{ flex:1, fontSize:12 }}>
          <span style={{ color: agentOnline ? C.greenSoft : C.txtMut, fontWeight:600 }}>
            {agentOnline ? "RMM Agent Online" : "Agent Offline"}
          </span>
          {device.last_seen && (
            <span style={{ color:C.txtFaint, marginLeft:10 }}>
              Last check-in {timeSince(device.last_seen)}
              {device.agent_version && ` · v${device.agent_version}`}
            </span>
          )}
          {!device.last_seen && (
            <span style={{ color:C.txtFaint, marginLeft:10 }}>Never checked in</span>
          )}
        </div>
        {h.health_score != null && (
          <div style={{ textAlign:"right", flexShrink:0 }}>
            <div style={{ fontSize:11, color:C.txtFaint, marginBottom:2 }}>Health score</div>
            <div style={{
              fontSize:18, fontWeight:700, lineHeight:1,
              color: h.health_score >= 80 ? C.green : h.health_score >= 60 ? C.amber : C.red,
            }}>
              {h.health_score}<span style={{ fontSize:11, color:C.txtFaint, fontWeight:400 }}>/100</span>
            </div>
          </div>
        )}
      </div>

      {/* Active health alerts */}
      {alertList.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:4, marginBottom:12 }}>
          {alertList.map((alert, i) => (
            <div key={i} style={{
              display:"flex", alignItems:"center", gap:8, padding:"7px 12px",
              background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.25)",
              borderRadius:8, fontSize:12, color:C.amberSoft,
            }}>
              <AlertTriangle size={12} color={C.amber}/>
              {alert}
            </div>
          ))}
        </div>
      )}

      {/* Vitals cards */}
      {hasVitals ? (
        <>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:8 }}>
            <StatCard
              icon={Cpu} label="CPU" pct={h.cpu_load_percent}
              value={h.cpu_load_percent != null ? `${h.cpu_load_percent.toFixed(0)}%` : "—"}
              sub={h.health_cpu_name || device.cpu_name || null}
            />
            <StatCard
              icon={MemoryStick} label="RAM" pct={h.ram_used_percent}
              value={h.ram_used_percent != null ? `${h.ram_used_percent.toFixed(0)}%` : "—"}
              sub={ramUsed != null && ramTotal != null ? `${ramUsed.toFixed(1)} / ${ramTotal.toFixed(1)} GB` : null}
            />
            <StatCard
              icon={HardDrive} label={hasAllDisks ? "C: Disk" : "Disk"} pct={h.disk_used_percent}
              value={h.disk_used_percent != null ? `${h.disk_used_percent.toFixed(0)}%` : "—"}
              sub={h.disk_used_gb != null && h.disk_total_gb != null ? `${h.disk_used_gb.toFixed(1)} / ${h.disk_total_gb.toFixed(1)} GB` : null}
            />
            {hasBattery && (
              <StatCard
                icon={h.battery_on_ac ? BatteryCharging : Battery}
                label="Battery" pct={h.battery_percent}
                value={h.battery_percent != null ? `${h.battery_percent}%` : "—"}
                sub={h.battery_on_ac ? "On AC power" : "On battery"}
                color={h.battery_percent >= 20 ? C.green : C.red}
              />
            )}
            <StatCard
              icon={Clock} label="Uptime"
              value={h.uptime_hours != null ? (h.uptime_hours >= 24 ? `${(h.uptime_hours/24).toFixed(1)}d` : `${h.uptime_hours.toFixed(1)}h`) : "—"}
              sub={h.last_boot ? `Since ${new Date(h.last_boot).toLocaleDateString(undefined, { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}` : null}
              color={h.uptime_hours != null && h.uptime_hours > 720 ? C.amber : C.txtSec}
            />
            <StatCard
              icon={User} label="Logged in"
              value={h.logged_in_user ? h.logged_in_user.split("\\").pop() : "—"}
              sub={h.logged_in_user && h.logged_in_user.includes("\\") ? h.logged_in_user.split("\\")[0] : null}
              color={C.txtSec}
            />
          </div>

          {/* Network I/O */}
          {hasNetwork && (
            <div style={{
              display:"flex", gap:16, padding:"10px 14px",
              background:C.bgCard, border:`1px solid ${C.bdSoft}`, borderRadius:10, marginBottom:8,
            }}>
              <Wifi size={13} color={C.txtFaint} style={{ marginTop:1, flexShrink:0 }}/>
              <div style={{ display:"flex", gap:24, fontSize:12 }}>
                <div>
                  <span style={{ color:C.txtFaint }}>↑ Sent</span>
                  <span style={{ color:C.txtSec, fontWeight:600, marginLeft:6 }}>
                    {h.net_sent_mb != null ? (h.net_sent_mb >= 1024 ? `${(h.net_sent_mb/1024).toFixed(1)} GB` : `${h.net_sent_mb.toFixed(0)} MB`) : "—"}
                  </span>
                </div>
                <div>
                  <span style={{ color:C.txtFaint }}>↓ Recv</span>
                  <span style={{ color:C.txtSec, fontWeight:600, marginLeft:6 }}>
                    {h.net_recv_mb != null ? (h.net_recv_mb >= 1024 ? `${(h.net_recv_mb/1024).toFixed(1)} GB` : `${h.net_recv_mb.toFixed(0)} MB`) : "—"}
                  </span>
                </div>
                <span style={{ color:C.txtFaint, fontSize:11 }}>since last boot</span>
              </div>
            </div>
          )}

          {/* AV status */}
          {h.av_product && (
            <div style={{
              display:"flex", alignItems:"center", gap:8, padding:"8px 14px",
              background:C.bgCard, border:`1px solid ${C.bdSoft}`, borderRadius:10, marginBottom:8,
              fontSize:12,
            }}>
              <Shield size={13} color={h.av_status === "enabled" ? C.green : C.red}/>
              <span style={{ color: h.av_status === "enabled" ? C.greenSoft : C.redSoft, fontWeight:600 }}>
                {h.av_product}
              </span>
              <span style={{ color:C.txtFaint }}>
                {h.av_status === "enabled" ? "real-time protection on" : "⚠ protection off"}
                {h.av_definitions_date ? ` · defs ${h.av_definitions_date}` : ""}
              </span>
              {h.pending_updates != null && h.pending_updates > 0 && (
                <span style={{ marginLeft:"auto", color:C.amberSoft }}>
                  {h.pending_updates} pending update{h.pending_updates !== 1 ? "s" : ""}
                  {h.last_patch_installed ? ` · last patch ${h.last_patch_installed}` : ""}
                </span>
              )}
            </div>
          )}

          {/* All drives */}
          {hasAllDisks && (
            <div style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, borderRadius:10, padding:"12px 14px", marginBottom:8 }}>
              <div style={{ fontSize:10, fontWeight:600, color:C.txtFaint, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:10 }}>
                All Drives
              </div>
              {allDisks.map((d, i) => <DriveBar key={i} drive={d}/>)}
            </div>
          )}
        </>
      ) : (
        !agentOnline && (
          <div style={{
            padding:"20px 16px", background:C.bgCard, border:`1px solid ${C.bdSoft}`,
            borderRadius:10, fontSize:12, color:C.txtFaint, textAlign:"center",
          }}>
            No vitals data — install the MIDTC RMM agent to enable monitoring.
          </div>
        )
      )}
    </div>
  );
}

// ─── RMM dispatch + poll ─────────────────────────────────────────────────────
async function rmmDispatchAndPoll(deviceId, type, payload, onResult) {
  const r = await dispatchRmmCommand(deviceId, type, payload);
  const id = r.data.id;
  for (let i = 0; i < 60; i++) {
    await new Promise(s => setTimeout(s, 3000));
    const got = await pollRmmCommand(id);
    if (got.data.status === "completed" || got.data.status === "failed") {
      onResult(got.data);
      return got.data;
    }
  }
  throw new Error("Agent did not respond in time. Command will execute on the next check-in.");
}

// ─── Tab: Terminal ────────────────────────────────────────────────────────────
function TerminalTab({ deviceId, rmmAvailable }) {
  const [history, setHistory] = useState([]);
  const [input, setInput]     = useState("");
  const [running, setRunning] = useState(false);
  const presets = [
    { label:"Get-Date",             cmd:"Get-Date" },
    { label:"Computer info",        cmd:"Get-ComputerInfo | Select-Object CsName,WindowsProductName,OsVersion,CsManufacturer,CsModel,BiosSerialNumber | Format-List" },
    { label:"Disk usage",           cmd:"Get-PSDrive -PSProvider FileSystem | Select-Object Name,@{n='UsedGB';e={[math]::Round($_.Used/1GB,1)}},@{n='FreeGB';e={[math]::Round($_.Free/1GB,1)}} | Format-Table" },
    { label:"Network adapters",     cmd:"Get-NetAdapter | Select-Object Name,Status,MacAddress,LinkSpeed | Format-Table" },
    { label:"Defender status",      cmd:"Get-MpComputerStatus | Select-Object AntivirusEnabled,RealTimeProtectionEnabled,AntivirusSignatureLastUpdated | Format-List" },
    { label:"Restart Spooler",      cmd:"Restart-Service -Name Spooler -Force; Write-Output 'Spooler restarted'" },
    { label:"Flush DNS",            cmd:"ipconfig /flushdns" },
    { label:"Last 10 System events",cmd:"Get-WinEvent -LogName System -MaxEvents 10 | Format-Table TimeCreated,LevelDisplayName,Id,Message -AutoSize" },
  ];
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 99999 }); }, [history]);

  const submit = async (command) => {
    const cmd = command || input;
    if (!cmd.trim() || !rmmAvailable || running) return;
    const entryId = Date.now();
    setHistory(h => [...h, { id:entryId, command:cmd, status:"pending" }]);
    setInput("");
    setRunning(true);
    try {
      const result = await rmmDispatchAndPoll(deviceId, "run_script", { language:"powershell", code:cmd }, (d) => {
        setHistory(h => h.map(item => item.id === entryId ? { ...item, status:d.status, output:d.output, error:d.error } : item));
      });
      if (!result) setHistory(h => h.map(item => item.id === entryId ? { ...item, status:"timeout", error:"No response — command queued" } : item));
    } catch (e) {
      setHistory(h => h.map(item => item.id === entryId ? { ...item, status:"failed", error:e.message } : item));
    }
    setRunning(false);
  };

  if (!rmmAvailable) return <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}>No RMM agent — install the MIDTC agent to enable remote execution.</div></Card>;

  return (
    <>
      <SectionLabel>Quick commands</SectionLabel>
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
        {presets.map(p => (
          <button key={p.label} onClick={() => submit(p.cmd)} disabled={running}
            style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"5px 10px",
                     borderRadius:6, fontSize:11, cursor:running?"not-allowed":"pointer", fontFamily:"inherit" }}>
            {p.label}
          </button>
        ))}
      </div>

      <SectionLabel>PowerShell / Bash terminal</SectionLabel>
      <Card style={{ padding:0, overflow:"hidden", marginBottom:12 }}>
        <div ref={scrollRef} style={{
          background:"#04080f", padding:14,
          fontFamily:"ui-monospace, 'SF Mono', Consolas, monospace",
          fontSize:12, color:C.txtSec, height:380, overflowY:"auto", lineHeight:1.5,
        }}>
          {history.length === 0 && <div style={{ color:C.txtFaint }}>Ready. Type a command and press Enter, or click a preset above.</div>}
          {history.map(item => (
            <div key={item.id} style={{ marginBottom:14 }}>
              <div style={{ color:C.blue4 }}>$ {item.command}</div>
              {item.status === "pending" && <div style={{ color:C.amber }}>  ⟳ waiting for agent check-in…</div>}
              {item.output && <pre style={{ margin:0, color:C.txtSec, whiteSpace:"pre-wrap", wordBreak:"break-word" }}>{item.output}</pre>}
              {item.error  && <pre style={{ margin:0, color:C.redSoft, whiteSpace:"pre-wrap" }}>error: {item.error}</pre>}
              {item.status && item.status !== "pending" && <div style={{ color:C.txtFaint, fontSize:10, marginTop:4 }}>[{item.status}]</div>}
            </div>
          ))}
        </div>
        <div style={{ display:"flex", borderTop:`1px solid ${C.bdSoft}`, background:"#04080f" }}>
          <span style={{ padding:"10px 12px", color:C.blue4, fontFamily:"ui-monospace, monospace", fontSize:13 }}>$</span>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
            placeholder="Get-Service | Where-Object Status -eq 'Running'"
            disabled={running}
            style={{ flex:1, padding:"10px 4px", background:"transparent", border:0, color:C.txtPri,
                     fontFamily:"ui-monospace, monospace", fontSize:12, outline:"none" }}/>
          <button onClick={() => submit()} disabled={!input.trim() || running}
            style={{ background:C.blue, color:"#fff", border:0, padding:"0 16px", cursor:input.trim()&&!running?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:4 }}>
            <Send size={12}/> Run
          </button>
        </div>
      </Card>
      <div style={{ fontSize:10, color:C.txtFaint }}>
        Commands execute on the next agent check-in (within 60 s for the C# agent) · 5 min timeout · output capped at 1 MB
      </div>
    </>
  );
}

// ─── Tab: Processes ───────────────────────────────────────────────────────────
function ProcessesTab({ deviceId, rmmAvailable }) {
  const isMobile = useIsMobile();
  const [procs, setProcs]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [killing, setKilling] = useState(null);
  const [filter, setFilter]   = useState("");

  const [capturedAt, setCapturedAt] = useState(null);
  const fetchProcs = async () => {
    if (!rmmAvailable) return;
    setLoading(true); setErr(null);
    try {
      await rmmDispatchAndPoll(deviceId, "get_processes", { tool_key: "processes" }, (d) => {
        if (d.status === "completed") {
          try { setProcs(JSON.parse(d.output)); setCapturedAt(new Date().toISOString()); }
          catch { setErr("Could not parse process list"); }
        } else { setErr(d.error || "Agent returned an error"); }
      });
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };
  // Seed from the server-side tool cache on mount so the tab renders instantly.
  useEffect(() => {
    if (!deviceId) return;
    getDeviceToolCache(deviceId)
      .then(r => {
        const row = (r.data || []).find(x => x.tool === "processes");
        if (row) { setProcs(row.payload); setCapturedAt(row.captured_at); }
      })
      .catch(() => {});
  }, [deviceId]);

  const killProc = async (pid, name) => {
    if (!window.confirm(`Kill process "${name}" (PID ${pid})?`)) return;
    setKilling(pid);
    try { await rmmDispatchAndPoll(deviceId, "kill_process", { pid }, () => {}); await fetchProcs(); }
    catch (e) { alert(e.message); }
    setKilling(null);
  };

  if (!rmmAvailable) return <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}>No RMM agent on this device.</div></Card>;
  const list  = Array.isArray(procs) ? procs : (procs ? [procs] : []);
  const fl    = filter.toLowerCase();
  const shown = fl ? list.filter(p => (p.name || "").toLowerCase().includes(fl)) : list;

  return (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, gap:8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <SectionLabel>Running processes — top 200 by memory</SectionLabel>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {capturedAt && !loading && <span style={{ fontSize:10, color:C.txtFaint }}>cached {relativeAge(capturedAt)}</span>}
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter…"
            style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"4px 8px", borderRadius:6, fontSize:11, width:140, outline:"none", fontFamily:"inherit" }}/>
          <button onClick={fetchProcs} disabled={loading}
            style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"5px 10px", borderRadius:6, fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
            <RefreshCw size={11}/> {loading ? "Querying…" : "Refresh"}
          </button>
        </div>
      </div>
      {err && <Card style={{ background:"rgba(239,68,68,0.08)", border:`1px solid ${C.red}33`, color:C.redSoft, fontSize:12 }}>{err}</Card>}
      {loading && !procs && <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}><RefreshCw size={14}/></div></Card>}
      {procs && (
        <Card style={{ padding:0, overflow:"hidden", maxHeight:600, overflowY:"auto", overflowX: isMobile ? "auto" : "hidden" }}>
          <table style={{ width:"100%", minWidth: isMobile ? 640 : undefined, borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:C.bgCardHi, position:"sticky", top:0 }}>
                {["Process","PID","CPU %","Memory","Path",""].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"8px 12px", color:C.txtFaint, fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((p, i) => (
                <tr key={i} style={{ borderTop:`1px solid ${C.bdSoft}` }}>
                  <td style={{ padding:"7px 12px", color:C.txtPri, fontFamily:"monospace" }}>{p.name}</td>
                  <td style={{ padding:"7px 12px", color:C.txtMut }}>{p.id}</td>
                  <td style={{ padding:"7px 12px", color:C.txtMut }}>{p.cpu != null ? p.cpu.toFixed(1) : "—"}</td>
                  <td style={{ padding:"7px 12px", color:C.txtSec }}>{p.ws_mb ? `${p.ws_mb} MB` : "—"}</td>
                  <td style={{ padding:"7px 12px", color:C.txtFaint, fontFamily:"monospace", fontSize:11, maxWidth:360, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.path || ""}</td>
                  <td style={{ padding:"7px 12px" }}>
                    <button onClick={() => killProc(p.id, p.name)} disabled={killing === p.id}
                      style={{ background:"rgba(239,68,68,0.12)", border:`1px solid ${C.red}44`, color:C.redSoft, padding:"3px 8px", borderRadius:5, fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>
                      {killing === p.id ? "…" : "Kill"}
                    </button>
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={6} style={{ padding:30, textAlign:"center", color:C.txtFaint }}>No results</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

// ─── Tab: Services ────────────────────────────────────────────────────────────
function ServicesTab({ deviceId, rmmAvailable }) {
  const isMobile = useIsMobile();
  const [svcs, setSvcs]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [filter, setFilter]       = useState("");
  const [actioning, setActioning] = useState(null);

  const [capturedAt, setCapturedAt] = useState(null);
  const fetchSvcs = async () => {
    if (!rmmAvailable) return;
    setLoading(true);
    try {
      await rmmDispatchAndPoll(deviceId, "get_services", { tool_key: "services" }, (d) => {
        if (d.status === "completed") {
          try { setSvcs(JSON.parse(d.output)); setCapturedAt(new Date().toISOString()); } catch {}
        }
      });
    } catch {}
    setLoading(false);
  };
  // Seed from the server-side tool cache on mount.
  useEffect(() => {
    if (!deviceId) return;
    getDeviceToolCache(deviceId)
      .then(r => {
        const row = (r.data || []).find(x => x.tool === "services");
        if (row) { setSvcs(row.payload); setCapturedAt(row.captured_at); }
      })
      .catch(() => {});
  }, [deviceId]);

  const serviceAction = async (name, action) => {
    setActioning(`${name}-${action}`);
    try { await rmmDispatchAndPoll(deviceId, "service_action", { name, action }, () => {}); await fetchSvcs(); }
    catch (e) { alert(e.message); }
    setActioning(null);
  };

  if (!rmmAvailable) return <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}>No RMM agent on this device.</div></Card>;
  const list  = Array.isArray(svcs) ? svcs : (svcs ? [svcs] : []);
  const fl    = filter.toLowerCase();
  const shown = fl ? list.filter(s => `${s.name} ${s.display_name}`.toLowerCase().includes(fl)) : list;

  return (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, gap:8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <SectionLabel>Services</SectionLabel>
        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
          {capturedAt && !loading && <span style={{ fontSize:10, color:C.txtFaint }}>cached {relativeAge(capturedAt)}</span>}
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter…"
            style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"4px 8px", borderRadius:6, fontSize:11, width:160, outline:"none", fontFamily:"inherit" }}/>
          <button onClick={fetchSvcs} disabled={loading}
            style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"5px 10px", borderRadius:6, fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
            <RefreshCw size={11}/> {loading ? "Querying…" : "Refresh"}
          </button>
        </div>
      </div>
      {loading && !svcs && <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}><RefreshCw size={14}/></div></Card>}
      {svcs && (
        <Card style={{ padding:0, overflow:"hidden", maxHeight:600, overflowY:"auto", overflowX: isMobile ? "auto" : "hidden" }}>
          <table style={{ width:"100%", minWidth: isMobile ? 560 : undefined, borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr style={{ background:C.bgCardHi, position:"sticky", top:0 }}>
                {["Service","Status","Start type","Actions"].map(h => (
                  <th key={h} style={{ textAlign:"left", padding:"8px 12px", color:C.txtFaint, fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((s, i) => {
                const busy = actioning?.startsWith(s.name + "-");
                return (
                  <tr key={i} style={{ borderTop:`1px solid ${C.bdSoft}` }}>
                    <td style={{ padding:"7px 12px" }}>
                      <div style={{ color:C.txtPri, fontFamily:"monospace" }}>{s.name}</div>
                      <div style={{ color:C.txtFaint, fontSize:11 }}>{s.display_name}</div>
                    </td>
                    <td style={{ padding:"7px 12px" }}>
                      {s.status === "Running" ? <Pill color={C.green}>running</Pill> :
                       s.status === "Stopped" ? <Pill color={C.txtMut}>stopped</Pill> :
                       <Pill color={C.amber}>{s.status}</Pill>}
                    </td>
                    <td style={{ padding:"7px 12px", color:C.txtMut, fontSize:11 }}>{s.start_type ?? "—"}</td>
                    <td style={{ padding:"7px 8px" }}>
                      <div style={{ display:"flex", gap:4 }}>
                        {s.status !== "Running" && (
                          <button onClick={() => serviceAction(s.name, "start")} disabled={busy}
                            style={{ background:"rgba(34,197,94,0.12)", border:`1px solid ${C.green}44`, color:C.greenSoft, padding:"3px 7px", borderRadius:5, fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>
                            {busy && actioning === `${s.name}-start` ? "…" : "Start"}
                          </button>
                        )}
                        {s.status === "Running" && (
                          <button onClick={() => serviceAction(s.name, "stop")} disabled={busy}
                            style={{ background:"rgba(148,163,184,0.12)", border:`1px solid ${C.bdSoft}`, color:C.txtMut, padding:"3px 7px", borderRadius:5, fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>
                            {busy && actioning === `${s.name}-stop` ? "…" : "Stop"}
                          </button>
                        )}
                        <button onClick={() => serviceAction(s.name, "restart")} disabled={busy}
                          style={{ background:"rgba(59,130,246,0.12)", border:`1px solid ${C.blue}44`, color:C.blue4, padding:"3px 7px", borderRadius:5, fontSize:10, cursor:"pointer", fontFamily:"inherit" }}>
                          {busy && actioning === `${s.name}-restart` ? "…" : "Restart"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={4} style={{ padding:30, textAlign:"center", color:C.txtFaint }}>No services match "{filter}"</td></tr>}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

// ─── Generic script-result tab ────────────────────────────────────────────────
function relativeAge(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  const m = Math.round(ms / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.round(h / 24)} d ago`;
}

function useRmmScriptResult(deviceId, psCode, rmmAvailable, toolKey) {
  const [data, setData]               = useState(null);
  const [err,  setErr]                = useState(null);
  const [loading, setLoading]         = useState(false);
  const [capturedAt, setCapturedAt]   = useState(null);

  // Seed from server-side cache on mount so tabs render instantly with the last
  // background-refresh snapshot. Cache is populated by the 5-min auto-refresh cron.
  useEffect(() => {
    if (!deviceId || !toolKey) return;
    getDeviceToolCache(deviceId)
      .then(r => {
        const row = (r.data || []).find(x => x.tool === toolKey);
        if (row) { setData(row.payload); setCapturedAt(row.captured_at); }
      })
      .catch(() => {});
  }, [deviceId, toolKey]);

  const run = async () => {
    if (!rmmAvailable) return;
    setLoading(true); setErr(null);
    try {
      await rmmDispatchAndPoll(deviceId, "run_script",
        { language:"powershell", code:psCode, ...(toolKey ? { tool_key: toolKey } : {}) },
        (d) => {
          if (d.status === "completed") {
            const json = (d.output || "").replace(/^ExitCode:\s*\d+\s*\r?\n/, "");
            try { setData(JSON.parse(json)); setCapturedAt(new Date().toISOString()); }
            catch { setErr("Could not parse agent response"); }
          } else { setErr(d.error || d.status); }
        });
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  return { data, err, loading, run, capturedAt };
}

function GenericQueryTab({ deviceId, rmmAvailable, psCode, columns, label, render, toolKey }) {
  const isMobile = useIsMobile();
  const { data, err, loading, run, capturedAt } = useRmmScriptResult(deviceId, psCode, rmmAvailable, toolKey);
  if (!rmmAvailable) return <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}>No RMM agent on this device.</div></Card>;
  const list = Array.isArray(data) ? data : (data ? [data] : []);
  return (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
        <SectionLabel>{label}</SectionLabel>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {capturedAt && !loading && (
            <span style={{ fontSize:10, color:C.txtFaint }}>cached {relativeAge(capturedAt)}</span>
          )}
          <button onClick={run} disabled={loading}
            style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"5px 10px", borderRadius:6, fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
            <RefreshCw size={11}/> {loading ? "Querying agent…" : "Refresh"}
          </button>
        </div>
      </div>
      {err && <Card style={{ background:"rgba(239,68,68,0.08)", border:`1px solid ${C.red}33`, color:C.redSoft, fontSize:12 }}>{err}</Card>}
      {loading && !data && <Card><div style={{ padding:30, textAlign:"center", color:C.txtFaint }}><RefreshCw size={14}/></div></Card>}
      {data && (
        <Card style={{ padding:0, overflow:"hidden", maxHeight:600, overflowY:"auto", overflowX: isMobile ? "auto" : "hidden" }}>
          <table style={{ width:"100%", minWidth: isMobile ? 640 : undefined, borderCollapse:"collapse", fontSize:12 }}>
            <thead><tr style={{ background:C.bgCardHi, position:"sticky", top:0 }}>
              {columns.map(c => <th key={c.key} style={{ textAlign:"left", padding:"8px 12px", color:C.txtFaint, fontSize:10, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em" }}>{c.label}</th>)}
            </tr></thead>
            <tbody>
              {list.length === 0
                ? <tr><td colSpan={columns.length} style={{ padding:30, textAlign:"center", color:C.txtFaint }}>No data.</td></tr>
                : list.map((row, i) => (
                    <tr key={i} style={{ borderTop:`1px solid ${C.bdSoft}` }}>
                      {columns.map(c => (
                        <td key={c.key} style={{ padding:"7px 12px", color:c.color || C.txtSec, fontFamily:c.mono?"monospace":"inherit", fontSize:c.fontSize || 12 }}>
                          {render?.[c.key]?.(row) ?? row[c.key] ?? "—"}
                        </td>
                      ))}
                    </tr>
                  ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

const PS_TASKS    = `Get-ScheduledTask | Where-Object State -ne 'Disabled' | Select-Object TaskPath,TaskName,State,@{n='Author';e={$_.Principal.UserId}} | ConvertTo-Json -Depth 2 -Compress`;
const PS_NETSTAT  = `Get-NetTCPConnection | Where-Object State -eq 'Established' | Select-Object LocalAddress,LocalPort,RemoteAddress,RemotePort,State,OwningProcess | ConvertTo-Json -Depth 2 -Compress`;
const PS_INSTALLED= `Get-Package | Select-Object Name,Version,@{n='Vendor';e={$_.Source}},ProviderName | Sort-Object Name | ConvertTo-Json -Depth 2 -Compress`;
const PS_EVENTS   = `Get-WinEvent -LogName System -MaxEvents 50 -ErrorAction SilentlyContinue | Select-Object TimeCreated,LevelDisplayName,ProviderName,Id,Message | ConvertTo-Json -Depth 2 -Compress`;

function TasksTab({ deviceId, rmmAvailable }) {
  return <GenericQueryTab deviceId={deviceId} rmmAvailable={rmmAvailable} psCode={PS_TASKS} toolKey="tasks"
    label="Scheduled tasks (active only)"
    columns={[
      { key:"TaskPath", label:"Path",   mono:true, fontSize:11, color:C.txtMut },
      { key:"TaskName", label:"Name",   color:C.txtPri, mono:true },
      { key:"State",    label:"State" },
      { key:"Author",   label:"Author", fontSize:11 },
    ]}/>;
}

function NetworkTab({ deviceId, rmmAvailable }) {
  return <GenericQueryTab deviceId={deviceId} rmmAvailable={rmmAvailable} psCode={PS_NETSTAT} toolKey="network"
    label="Established TCP connections"
    columns={[
      { key:"LocalAddress",  label:"Local",  mono:true },
      { key:"LocalPort",     label:"Port",   mono:true, fontSize:11 },
      { key:"RemoteAddress", label:"Remote", mono:true },
      { key:"RemotePort",    label:"Port",   mono:true, fontSize:11 },
      { key:"State",         label:"State" },
      { key:"OwningProcess", label:"PID",    mono:true, fontSize:11 },
    ]}/>;
}

function InstalledTab({ deviceId, rmmAvailable }) {
  return <GenericQueryTab deviceId={deviceId} rmmAvailable={rmmAvailable} psCode={PS_INSTALLED} toolKey="installed"
    label="Installed software"
    columns={[
      { key:"Name",         label:"Name",     color:C.txtPri },
      { key:"Version",      label:"Version",  mono:true, fontSize:11, color:C.txtMut },
      { key:"Vendor",       label:"Source",   fontSize:11 },
      { key:"ProviderName", label:"Provider", fontSize:11, color:C.txtFaint },
    ]}/>;
}

function EventsTab({ deviceId, rmmAvailable }) {
  return <GenericQueryTab deviceId={deviceId} rmmAvailable={rmmAvailable} psCode={PS_EVENTS} toolKey="events"
    label="System log — most recent 50 entries"
    columns={[
      { key:"TimeCreated",      label:"When",    fontSize:11, color:C.txtMut },
      { key:"LevelDisplayName", label:"Level" },
      { key:"ProviderName",     label:"Provider",fontSize:11 },
      { key:"Id",               label:"ID",      mono:true,   fontSize:11 },
      { key:"Message",          label:"Message", fontSize:11 },
    ]}
    render={{
      Message:     r => <span style={{ display:"inline-block", maxWidth:480, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }} title={r.Message}>{r.Message}</span>,
      TimeCreated: r => r.TimeCreated ? new Date(r.TimeCreated).toLocaleString() : "—",
    }}/>;
}

// ─── Tab: Install Software ────────────────────────────────────────────────────
function InstallTab({ deviceId, rmmAvailable }) {
  const isMobile = useIsMobile();
  const [pkg, setPkg]         = useState("");
  const [mgr, setMgr]         = useState("winget");
  const [running, setRunning] = useState(false);
  const [result, setResult]   = useState(null);

  const install = async () => {
    if (!pkg.trim() || !rmmAvailable || running) return;
    setRunning(true); setResult(null);
    try {
      await rmmDispatchAndPoll(deviceId, "install_software", { package_manager:mgr, package_name:pkg.trim() }, (d) => { setResult(d); });
    } catch (e) { setResult({ status:"failed", error:e.message }); }
    setRunning(false);
  };

  if (!rmmAvailable) return <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}>No RMM agent on this device.</div></Card>;

  return (
    <>
      <SectionLabel>Install software on this device</SectionLabel>
      <Card style={{ marginBottom:14 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <select value={mgr} onChange={e => setMgr(e.target.value)}
            style={{ background:C.bgCardHi, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"7px 10px", borderRadius:6, fontSize:12, fontFamily:"inherit", outline:"none" }}>
            <option value="winget">winget (Windows)</option>
            <option value="brew">brew (macOS)</option>
            <option value="choco">choco (Chocolatey)</option>
          </select>
          <input value={pkg} onChange={e => setPkg(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") install(); }}
            placeholder={mgr === "winget" ? "e.g. Google.Chrome" : mgr === "brew" ? "e.g. firefox" : "e.g. googlechrome"}
            style={{ flex: isMobile ? "1 1 100%" : 1, background:C.bgCardHi, border:`1px solid ${C.bdSoft}`, color:C.txtPri, padding:"7px 10px", borderRadius:6, fontSize:12, fontFamily:"monospace", outline:"none" }}/>
          <button onClick={install} disabled={!pkg.trim() || running}
            style={{ background:C.blue, color:"#fff", border:0, padding:"7px 16px", borderRadius:6, fontSize:12, fontWeight:600, cursor:pkg.trim()&&!running?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:6, fontFamily:"inherit" }}>
            <Download size={13}/> {running ? "Installing…" : "Install"}
          </button>
        </div>
        <div style={{ fontSize:11, color:C.txtFaint }}>
          The agent will run the package manager on the device. Installation may take several minutes.
        </div>
      </Card>
      {running && (
        <Card style={{ background:"rgba(59,130,246,0.06)", border:`1px solid ${C.blue}33` }}>
          <div style={{ color:C.blue4, fontSize:12 }}>⟳ Waiting for agent to pick up the install command…</div>
        </Card>
      )}
      {result && (
        <>
          <SectionLabel>Install result · {result.status}</SectionLabel>
          <Card style={{ padding:0 }}>
            <pre style={{ background:"#04080f", padding:14, fontFamily:"ui-monospace, monospace", fontSize:11, color:result.status==="completed"?C.greenSoft:C.redSoft, margin:0, whiteSpace:"pre-wrap", wordBreak:"break-word", maxHeight:300, overflowY:"auto" }}>
              {result.output || result.error || "(no output)"}
            </pre>
          </Card>
        </>
      )}
    </>
  );
}

// ─── Tab: Scripts ─────────────────────────────────────────────────────────────
function ScriptsTab({ deviceId, rmmAvailable }) {
  const [scripts, setScripts]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [running, setRunning]   = useState(null);
  const [result,  setResult]    = useState(null);
  const [adding,  setAdding]    = useState(false);
  const [newScript, setNewScript] = useState({ name:"", description:"", language:"powershell", code:"" });

  const refresh = async () => { setLoading(true); try { const r = await getRmmScripts(); setScripts(r.data || []); } catch {} setLoading(false); };
  useEffect(() => { refresh(); }, []);

  const run = async (s) => {
    if (!rmmAvailable) return;
    setRunning(s.id); setResult(null);
    try {
      await rmmDispatchAndPoll(deviceId, "run_script", { language:s.language || "powershell", code:s.code }, (d) => { setResult({ name:s.name, ...d }); });
    } catch (e) { setResult({ name:s.name, status:"failed", error:e.message }); }
    setRunning(null);
  };

  const saveScript = async () => {
    if (!newScript.name || !newScript.code) return;
    try { await saveRmmScript(newScript); setNewScript({ name:"", description:"", language:"powershell", code:"" }); setAdding(false); refresh(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const deleteScript = async (id) => {
    if (!window.confirm("Delete this script?")) return;
    try { await deleteRmmScript(id); refresh(); } catch (e) { alert(e.message); }
  };

  if (!rmmAvailable) return <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}>No RMM agent on this device.</div></Card>;

  return (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <SectionLabel>Saved scripts — one-click execute on this device</SectionLabel>
        <button onClick={() => setAdding(a => !a)}
          style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"5px 10px", borderRadius:6, fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", gap:4 }}>
          <Plus size={11}/> New script
        </button>
      </div>

      {adding && (
        <Card style={{ marginBottom:12 }}>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            <div style={{ display:"flex", gap:8 }}>
              <input value={newScript.name} onChange={e => setNewScript(s => ({ ...s, name:e.target.value }))} placeholder="Script name"
                style={{ flex:1, background:C.bgCardHi, border:`1px solid ${C.bdSoft}`, color:C.txtPri, padding:"6px 10px", borderRadius:6, fontSize:12, fontFamily:"inherit", outline:"none" }}/>
              <select value={newScript.language} onChange={e => setNewScript(s => ({ ...s, language:e.target.value }))}
                style={{ background:C.bgCardHi, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"6px 10px", borderRadius:6, fontSize:12, fontFamily:"inherit", outline:"none" }}>
                <option value="powershell">PowerShell</option>
                <option value="bash">Bash (macOS/Linux)</option>
              </select>
            </div>
            <input value={newScript.description} onChange={e => setNewScript(s => ({ ...s, description:e.target.value }))} placeholder="Description (optional)"
              style={{ background:C.bgCardHi, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"6px 10px", borderRadius:6, fontSize:12, fontFamily:"inherit", outline:"none" }}/>
            <textarea value={newScript.code} onChange={e => setNewScript(s => ({ ...s, code:e.target.value }))} placeholder="Script code…" rows={6}
              style={{ background:"#04080f", border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"8px 10px", borderRadius:6, fontSize:12, fontFamily:"ui-monospace,monospace", outline:"none", resize:"vertical" }}/>
            <div style={{ display:"flex", gap:6, justifyContent:"flex-end" }}>
              <button onClick={() => setAdding(false)}
                style={{ background:"transparent", border:`1px solid ${C.bdSoft}`, color:C.txtMut, padding:"5px 12px", borderRadius:6, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>
                Cancel
              </button>
              <button onClick={saveScript} disabled={!newScript.name || !newScript.code}
                style={{ background:C.blue, color:"#fff", border:0, padding:"5px 14px", borderRadius:6, fontSize:11, fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                Save script
              </button>
            </div>
          </div>
        </Card>
      )}

      {loading
        ? <Card><div style={{ padding:30, textAlign:"center", color:C.txtFaint }}><RefreshCw size={14}/></div></Card>
        : scripts.length === 0
          ? <Card><div style={{ padding:30, textAlign:"center", color:C.txtFaint, fontSize:12 }}>No saved scripts. Click "New script" to add one.</div></Card>
          : (
            <Card style={{ padding:0, overflow:"hidden", marginBottom:14 }}>
              {scripts.map(s => (
                <div key={s.id} style={{ borderBottom:`1px solid ${C.bdSoft}`, padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
                  <FileText size={14} color={C.txtMut} style={{ flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, color:C.txtPri, fontWeight:500 }}>{s.name}</div>
                    <div style={{ fontSize:11, color:C.txtFaint, marginTop:2 }}>
                      {s.language}{s.description ? ` · ${s.description}` : ""}{s.created_by_name ? ` · by ${s.created_by_name}` : ""}
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                    <button onClick={() => run(s)} disabled={running === s.id || !rmmAvailable}
                      style={{ background:running===s.id?C.bgCardHi:C.blue, color:"#fff", border:0, padding:"6px 12px", borderRadius:6, fontSize:11, cursor:"pointer", display:"flex", alignItems:"center", gap:4, fontWeight:600, fontFamily:"inherit" }}>
                      <Play size={11}/> {running === s.id ? "Running…" : "Run"}
                    </button>
                    <button onClick={() => deleteScript(s.id)}
                      style={{ background:"transparent", border:`1px solid ${C.bdSoft}`, color:C.txtMut, padding:"6px 8px", borderRadius:6, fontSize:11, cursor:"pointer", fontFamily:"inherit" }}>
                      <Trash2 size={11}/>
                    </button>
                  </div>
                </div>
              ))}
            </Card>
          )}

      {result && (
        <>
          <SectionLabel>Last run · {result.name} · {result.status}</SectionLabel>
          <Card style={{ padding:0, overflow:"hidden" }}>
            <pre style={{ background:"#04080f", padding:14, fontFamily:"ui-monospace, monospace", fontSize:11, color:C.txtSec, margin:0, maxHeight:300, overflowY:"auto", whiteSpace:"pre-wrap", wordBreak:"break-word" }}>
              {result.output || result.error || "(no output)"}
            </pre>
          </Card>
        </>
      )}
    </>
  );
}

// ─── Tab: Alerts ──────────────────────────────────────────────────────────────
function AlertsTab({ deviceId }) {
  const [alerts, setAlerts]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showResolved, setShowResolved] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { const r = await getDeviceAlerts(deviceId, showResolved); setAlerts(r.data || []); } catch {}
    setLoading(false);
  };
  useEffect(() => { refresh(); }, [deviceId, showResolved]);

  return (
    <>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
        <SectionLabel>Device alerts {showResolved ? "(all)" : "(active)"}</SectionLabel>
        <label style={{ display:"flex", alignItems:"center", gap:5, color:C.txtMut, fontSize:11, cursor:"pointer" }}>
          <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)}/> Show resolved
        </label>
      </div>
      {loading
        ? <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}><RefreshCw size={14}/></div></Card>
        : alerts.length === 0
          ? <Card><div style={{ color:C.txtFaint, fontSize:12, padding:20, textAlign:"center" }}>{showResolved ? "No alerts on file." : "No active alerts."}</div></Card>
          : (
            <Card style={{ padding:0, overflow:"hidden" }}>
              {alerts.map(a => (
                <div key={a.id} style={{ borderBottom:`1px solid ${C.bdSoft}`, padding:"10px 14px", display:"flex", alignItems:"center", gap:10, boxSizing:"border-box" }}>
                  <AlertTriangle size={14} color={a.resolved ? C.txtMut : C.amber} style={{ flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, color:a.resolved ? C.txtMut : C.txtPri, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      <strong>{a.process_name || "process"}</strong>
                      {a.cpu_percent != null  && ` · CPU ${Math.round(a.cpu_percent)}%`}
                      {a.memory_mb   != null  && ` · ${a.memory_mb} MB`}
                    </div>
                    <div style={{ fontSize:11, color:C.txtFaint, marginTop:2 }}>
                      {timeSince(a.alerted_at)}{a.resolved && ` · resolved ${timeSince(a.resolved_at)}`}
                    </div>
                  </div>
                  {!a.resolved && (
                    <button onClick={() => resolveDeviceAlert(deviceId, a.id).then(refresh)}
                      style={{ background:"transparent", border:`1px solid ${C.bdSoft}`, color:C.txtMut, padding:"3px 8px", borderRadius:5, fontSize:11, cursor:"pointer", flexShrink:0, fontFamily:"inherit" }}>
                      Mark resolved
                    </button>
                  )}
                </div>
              ))}
            </Card>
          )}
    </>
  );
}

// ─── Tab: Device Info ─────────────────────────────────────────────────────────
function InfoTab({ device }) {
  return (
    <>
      <SectionLabel>Device identity</SectionLabel>
      <Card style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px 24px", marginBottom:16, fontSize:12 }}>
        {[
          ["Hostname",      device.hostname || device.device_name],
          ["OS",            device.os_version ? `${device.os || "Windows"} (${device.os_version})` : (device.os || "—")],
          ["Manufacturer",  device.manufacturer || "—"],
          ["Model",         device.model || "—"],
          ["Serial number", device.serial_number || "—"],
          ["MAC address",   device.mac_address || "—"],
          ["IP address",    device.ip_address || "—"],
          ["Type",          device.device_type || "workstation"],
          ["Client",        device.client_name || "—"],
          ["Assigned to",   device.contact_name || "unassigned"],
          ["Last seen",     timeSince(device.last_seen)],
          ["Agent version", device.agent_version || "—"],
        ].map(([k, v], i) => (
          <div key={i} style={{ display:"flex", justifyContent:"space-between", borderBottom:`1px solid ${C.bdSoft}`, paddingBottom:6 }}>
            <span style={{ color:C.txtMut }}>{k}</span>
            <span style={{ color:C.txtSec, fontFamily:["Hostname","Serial number","MAC address","IP address"].includes(k)?"monospace":"inherit", fontSize:12 }}>{v}</span>
          </div>
        ))}
      </Card>

      <SectionLabel>Vendor &amp; warranty</SectionLabel>
      <Card style={{ marginBottom:16 }}>
        {device.warranty_expires ? (
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, color:C.txtPri }}>
                Warranty {new Date(device.warranty_expires) > new Date() ? "active" : "expired"}
              </div>
              <div style={{ fontSize:11, color:C.txtMut, marginTop:3 }}>
                Expires {new Date(device.warranty_expires).toLocaleDateString()}
                {device.purchase_date && ` · Purchased ${new Date(device.purchase_date).toLocaleDateString()}`}
              </div>
            </div>
            {new Date(device.warranty_expires) > new Date()
              ? <Pill color={C.green}>covered</Pill>
              : <Pill color={C.red}>expired</Pill>}
          </div>
        ) : (
          <div style={{ fontSize:12, color:C.txtFaint }}>
            No warranty on file. {device.serial_number ? `Serial: ${device.serial_number}` : "Serial number missing."}
          </div>
        )}
      </Card>
    </>
  );
}

// ─── Power controls ───────────────────────────────────────────────────────────
function PowerControls({ deviceId, rmmAvailable }) {
  const fire = async (label, ps) => {
    if (!window.confirm(`${label} this device?`)) return;
    try { await dispatchRmmCommand(deviceId, "run_script", { language:"powershell", code:ps }); }
    catch (e) { alert(e.response?.data?.error || e.message); }
  };
  return (
    <div style={{ display:"flex", gap:6 }}>
      <button onClick={() => fire("Lock", "rundll32.exe user32.dll,LockWorkStation")} disabled={!rmmAvailable}
        style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.txtSec, padding:"6px 10px", borderRadius:7, fontSize:12, cursor:rmmAvailable?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:4 }}>
        <Lock size={11}/> Lock
      </button>
      <button onClick={() => fire("Reboot", "Restart-Computer -Force")} disabled={!rmmAvailable}
        style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.amberSoft, padding:"6px 10px", borderRadius:7, fontSize:12, cursor:rmmAvailable?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:4 }}>
        <RefreshCcw size={11}/> Reboot
      </button>
      <button onClick={() => fire("Shut down", "Stop-Computer -Force")} disabled={!rmmAvailable}
        style={{ background:C.bgCard, border:`1px solid ${C.bdSoft}`, color:C.redSoft, padding:"6px 10px", borderRadius:7, fontSize:12, cursor:rmmAvailable?"pointer":"not-allowed", display:"flex", alignItems:"center", gap:4 }}>
        <Power size={11}/> Shut down
      </button>
    </div>
  );
}

// ─── Mobile command panel ───────────────────────────────────────────────────
// Phones/tablets get IT actions instead of the Windows tool tabs. Android rows
// (with the combined-app agent) accept the mobile_* commands via the same
// dispatch/checkin path; iOS rows are MDM-managed (lock/wipe go through the
// MDM channel — surfaced here, dispatched server-side in a later pass).
function MobileActions({ device, isAndroid, rmmAvailable }) {
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState(null);

  const send = async (type, label, payload = {}, confirm = false) => {
    if (confirm && !window.confirm(`${label} on ${device.device_name || "this device"}?`)) return;
    setBusy(type); setNote(null);
    try {
      await dispatchRmmCommand(device.id, type, payload);
      setNote({ ok: true, msg: `${label} sent — the device runs it on its next check-in.` });
    } catch (e) {
      setNote({ ok: false, msg: e.response?.data?.error || e.message });
    } finally { setBusy(null); }
  };

  const sendMessage = () => {
    const msg = window.prompt("Message to show on the device:");
    if (msg) send("mobile_message", "Message", { message: msg });
  };

  const Btn = ({ type, icon: Icon, label, color, onClick, danger }) => (
    <button
      onClick={onClick || (() => send(type, label, {}, danger))}
      disabled={busy != null || !rmmAvailable}
      style={{
        background: C.bgCard, border:`1px solid ${danger ? "rgba(239,68,68,0.4)" : C.bdSoft}`,
        color: color || C.txtSec, padding:"9px 14px", borderRadius:9, fontSize:13,
        cursor: (busy != null || !rmmAvailable) ? "not-allowed" : "pointer",
        display:"flex", alignItems:"center", gap:7, fontFamily:"inherit",
        opacity: (busy != null || !rmmAvailable) ? 0.6 : 1,
      }}>
      <Icon size={14}/> {busy === type ? "Sending…" : label}
    </button>
  );

  return (
    <div style={{ background:C.bgWindow, border:`1px solid ${C.bdSoft}`, borderRadius:14, padding:"16px 18px", marginBottom:16 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
        <MonitorSmartphone size={15} color={C.blue}/>
        <span style={{ fontSize:13, fontWeight:600, color:C.txtPri }}>
          {isAndroid ? "Mobile Actions (Android agent)" : "Mobile Actions (iOS / MDM)"}
        </span>
        {!rmmAvailable && <span style={{ fontSize:11, color:C.amberSoft }}>· device offline</span>}
      </div>

      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        <Btn type="mobile_lock"    icon={Lock}  label="Lock"            danger />
        <Btn type="mobile_message" icon={Send}  label="Send Message"   onClick={sendMessage} />
        {isAndroid && <Btn type="mobile_ring"   icon={Bell}   label="Ring" />}
        {isAndroid && <Btn type="mobile_locate" icon={MapPin} label="Locate" />}
        {isAndroid && <Btn type="mobile_rustdesk" icon={MonitorSmartphone} label="Remote (RustDesk)" />}
        <Btn type="mobile_wipe"    icon={Trash2} label="Wipe Device" color={C.redSoft} danger />
      </div>

      {note && (
        <div style={{ marginTop:12, fontSize:12, color: note.ok ? "#34d399" : C.redSoft }}>
          {note.msg}
        </div>
      )}
      {!isAndroid && (
        <div style={{ marginTop:12, fontSize:11, color:C.txtFaint, lineHeight:1.5 }}>
          iOS is managed over Apple's MDM channel — lock and wipe are supported; live metrics and
          remote control are not available on iOS (Apple platform restriction).
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function Device360() {
  const { deviceId } = useParams();
  const navigate     = useNavigate();
  const isMobile     = useIsMobile();
  const [device, setDevice]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState("terminal");

  const refresh = async () => {
    setLoading(true);
    try {
      const d = await getDevice(deviceId).then(r => r.data).catch(() => null);
      if (d) setDevice(d);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { refresh(); }, [deviceId]);

  if (loading && !device) return (
    <div style={{ minHeight:"100vh", background:C.bgPage, display:"grid", placeItems:"center", color:C.txtFaint }}>
      <RefreshCw size={20}/>
    </div>
  );
  if (!device) return <div style={{ padding:60, textAlign:"center", color:C.redSoft }}>Device not found</div>;

  const rmmAvailable = device.last_seen && (Date.now() - new Date(device.last_seen).getTime()) < 48 * 3600_000;

  // Phones/tablets: the Windows/PowerShell tool tabs don't apply. Show a Mobile
  // Actions panel and only the Alerts + Device Info tabs. iOS rows (hardware_id
  // mdm-*) are MDM-managed; Android rows (mobile-* with an agent) accept the
  // mobile_* commands.
  const isMobileDevice = String(device.device_type).toLowerCase() === "mobile"
    || /android|ios/i.test(device.os || "");
  const isAndroid = /android/i.test(device.os || "");
  // Macs enrol (and get RustDesk) only via the installer, so surface it on the
  // device itself — this is the page a tech is on when a Mac looks wrong.
  const isMac = /mac|darwin|os x/i.test(`${device.os || ""} ${device.os_version || ""}`);

  const tabs = isMobileDevice ? [
    { id:"alerts",     label:"Alerts",     icon:AlertTriangle },
    { id:"info",       label:"Device Info",icon:Info },
  ] : [
    { id:"terminal",   label:"Terminal",   icon:Terminal },
    { id:"scripts",    label:"Scripts",    icon:BookOpen },
    { id:"processes",  label:"Processes",  icon:Activity },
    { id:"services",   label:"Services",   icon:Server },
    { id:"install",    label:"Install",    icon:Download },
    { id:"tasks",      label:"Tasks",      icon:Calendar },
    { id:"network",    label:"Network",    icon:Network },
    { id:"installed",  label:"Installed",  icon:Package },
    { id:"events",     label:"Events",     icon:FileText },
    { id:"alerts",     label:"Alerts",     icon:AlertTriangle },
    { id:"info",       label:"Device Info",icon:Info },
  ];

  return (
    <div style={{
      minHeight:"100vh",
      background:`radial-gradient(circle at 20% -10%, rgba(59,130,246,0.06), transparent 50%),
                   radial-gradient(circle at 100% 100%, rgba(168,85,247,0.04), transparent 40%),
                   ${C.bgPage}`,
      fontFamily:"'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      color:C.txtPri, padding: isMobile ? "14px" : "20px 24px",
      overflowX:"hidden", boxSizing:"border-box",
    }}>
      <div style={{ maxWidth:1200, margin:"0 auto", boxSizing:"border-box" }}>

        {/* Top bar */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? 8 : 0 }}>
          <button onClick={() => navigate("/devices")}
            style={{ background:"transparent", border:0, color:C.txtMut, cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:12, fontFamily:"inherit" }}>
            <ArrowLeft size={14}/> All devices
          </button>
          {!isMobileDevice && <PowerControls deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        </div>

        {/* Title card */}
        <div style={{
          background:C.bgWindow, border:`1px solid ${C.bdSoft}`, borderRadius:14,
          padding:"18px 22px", marginBottom:16,
          backgroundImage:`linear-gradient(180deg, ${C.bgCardHi} 0%, ${C.bgWindow} 100%)`,
        }}>
          <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", alignItems:"flex-start", justifyContent:"space-between", gap:16 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:11, color:C.txtFaint, fontWeight:500, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>Device 360</div>
              <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:C.txtPri, letterSpacing:"-0.01em", fontFamily:"monospace" }}>
                {device.hostname || device.device_name}
              </h1>
              <div style={{ marginTop:6, fontSize:12, color:C.txtMut }}>
                {[device.client_name, device.os_version || device.os, device.model].filter(Boolean).join(" · ")}
              </div>
            </div>
            {!isMobileDevice && (
              <div style={{ flexShrink:0, paddingTop:4, display:"flex", gap:8, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                <RemoteButton
                  mode="rustdesk"
                  deviceId={device.id}
                  deviceName={device.hostname || device.device_name}
                  contactName={device.contact_name || device.client_name}
                  rustdeskId={device.rustdesk_id}
                />
                <RemoteButton
                  mode="mesh"
                  deviceId={device.id}
                  deviceName={device.hostname || device.device_name}
                  contactName={device.contact_name || device.client_name}
                  meshcentralNodeId={device.meshcentral_node_id}
                />
              </div>
            )}
          </div>
        </div>

        {/* macOS install / repair. Shown on every Mac, because the same one-liner
            both enrols a new machine and revives one whose agent has died — and a
            dead agent is precisely why someone would be looking at this page. The
            note calls out a missing RustDesk ID, since that is what leaves the
            Remote button above inert. */}
        {isMac && (
          <div style={{ marginBottom:16 }}>
            <MacAgentInstall
              compact
              palette={{
                panel: C.bgCard, border: C.bdSoft, text: C.txtPri,
                muted: C.txtMut, faint: C.txtFaint, accent: C.blue4,
              }}
              note={!device.rustdesk_id
                ? "No RustDesk ID on this Mac yet, so remote control is unavailable. Run this on the machine — it installs or repairs the signed agent, registers it, and RustDesk follows on the first check-in."
                : null}
            />
          </div>
        )}

        {/* Mobile command panel (phones/tablets) — replaces the Windows tool tabs */}
        {isMobileDevice && (
          <MobileActions device={device} isAndroid={isAndroid} rmmAvailable={rmmAvailable}/>
        )}

        {/* Vitals panel — always visible */}
        <VitalsPanel device={device}/>

        {/* Tool tabs */}
        <div style={{ display:"flex", gap:2, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : "visible", borderBottom:`1px solid ${C.bdSoft}`, marginBottom:16 }}>
          {tabs.map(t => {
            const Icon  = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{
                  background:"transparent", border:0,
                  color:active ? C.txtPri : C.txtMut,
                  borderBottom:`2px solid ${active ? C.blue : "transparent"}`,
                  padding:"9px 13px", fontSize:12.5, fontWeight:active ? 600 : 500,
                  cursor:"pointer", display:"flex", alignItems:"center", gap:7,
                  fontFamily:"inherit", whiteSpace:"nowrap",
                }}>
                <Icon size={13}/> {t.label}
              </button>
            );
          })}
        </div>

        {/* On mobile devices, snap an out-of-range tab (default "terminal") to a valid one */}
        {(() => { if (isMobileDevice && !tabs.some(t => t.id === tab)) { setTab("info"); } return null; })()}

        {tab === "terminal"  && <TerminalTab   deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "scripts"   && <ScriptsTab    deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "processes" && <ProcessesTab  deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "services"  && <ServicesTab   deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "install"   && <InstallTab    deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "tasks"     && <TasksTab      deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "network"   && <NetworkTab    deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "installed" && <InstalledTab  deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "events"    && <EventsTab     deviceId={deviceId} rmmAvailable={rmmAvailable}/>}
        {tab === "alerts"    && <AlertsTab     deviceId={deviceId}/>}
        {tab === "info"      && <InfoTab       device={device}/>}

      </div>
    </div>
  );
}
